import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Nav, Row, Tab, Table } from 'react-bootstrap';
import { FiArrowDown, FiArrowUp, FiTrash2, FiEdit2 } from 'react-icons/fi';
import {
  ResourceField,
  ResourceFormPage,
  SelectOption,
} from '../../components/resource/ResourceFormPage';
import {
  catalogoProdutosService,
  fichaTecnicaService,
  maquinasService,
  unidadesMedidaService,
} from '../../services';
import { FichaEtapa, FichaInsumo, FichaSubprocesso, FichaSubproduto, FichaTecnica } from '../../types/fichas';
import { Maquina } from '../../types/producao';
import { ProdutoCatalogo } from '../../types/estoque';
import { UnidadeMedida } from '../../types/aquisicao';
import { makeOptionLoader } from '../../utils/optionLoaders';

const tabs = [
  { key: 'etapas', label: 'Etapas' },
  { key: 'insumos', label: 'Insumos' },
  { key: 'subprocessos', label: 'Subprocessos' },
  { key: 'subprodutos', label: 'Subprodutos' },
] as const;

interface EtapaDraft {
  sequencia: string;
  descricao: string;
  observacao: string;
  maquina?: number | null;
  tempo_setup_maquina: string;
  tempo_maquina_min: string;
  tempo_homem_min: string;
  tempo_espera_min: string;
  qtd_operadores: string;
}

const buildDraft = (sequence: number): EtapaDraft => ({
  sequencia: String(sequence),
  descricao: '',
  observacao: '',
  maquina: undefined,
  tempo_setup_maquina: '',
  tempo_maquina_min: '',
  tempo_homem_min: '',
  tempo_espera_min: '',
  qtd_operadores: '',
});

interface SubprocessoDraft {
  sequencia: string;
  sub_ficha?: number | null;
  quantidade: string;
  unidade?: number | null;
  observacao: string;
}

const buildSubprocessoDraft = (): SubprocessoDraft => ({
  sequencia: '1',
  sub_ficha: undefined,
  quantidade: '',
  unidade: undefined,
  observacao: '',
});

interface SubprodutoDraft {
  produto?: number | null;
  percentual_saida: string;
  tipo_subproduto?: 'REAP' | 'DESC' | 'VEND';
  quantidade_estimada: string;
  unidade?: number | null;
  valor_venda: string;
  observacao: string;
}

const buildSubprodutoDraft = (): SubprodutoDraft => ({
  produto: undefined,
  percentual_saida: '',
  tipo_subproduto: undefined,
  quantidade_estimada: '',
  unidade: undefined,
  valor_venda: '',
  observacao: '',
});

interface InsumoDraft {
  produto?: number | null;
  quantidade_teorica: string;
  unidade?: number | null;
  fator_aproveitamento: string;
  observacao: string;
}

const buildInsumoDraft = (): InsumoDraft => ({
  produto: undefined,
  quantidade_teorica: '',
  unidade: undefined,
  fator_aproveitamento: '',
  observacao: '',
});

const parseDecimalInput = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }
  const normalized = value.replace(/\s+/g, '').replace(',', '.');
  if (!normalized) {
    return undefined;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeIntegerMinute = (value?: string): string => {
  if (!value) {
    return '';
  }
  const normalized = value.replace(/\s+/g, '').replace(',', '.');
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return '';
  }
  return String(Math.max(0, Math.round(parsed)));
};

const produtoLabel = (item: ProdutoCatalogo) => {
  const parts = [];
  if (item.codigo) parts.push(item.codigo);
  if (item.descricao) parts.push(item.descricao);
  return parts.join(' - ') || String(item.id);
};

const produtoOptionLoader = makeOptionLoader<ProdutoCatalogo>(
  catalogoProdutosService.list,
  produtoLabel,
);

const EtapasTabContent: React.FC<{
  formData: Record<string, unknown>;
  setFieldValue: (name: string, value: unknown) => void;
}> = ({ formData, setFieldValue }) => {
  const etapas = Array.isArray(formData.etapas) ? (formData.etapas as FichaEtapa[]) : [];
  const [machines, setMachines] = useState<Maquina[]>([]);
  const [draft, setDraft] = useState<EtapaDraft>(() => buildDraft(etapas.length + 1));
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    maquinasService
      .list()
      .then((response) => setMachines(response.results ?? []))
      .catch(() => setMachines([]));
  }, []);

  useEffect(() => {
    setDraft((prev) => ({
      ...prev,
      sequencia: String(etapas.length + 1),
    }));
  }, [etapas.length]);

  const normalizedMachines = machines.reduce<Record<number, string>>((acc, machine) => {
    if (machine.id !== undefined) {
      acc[machine.id] = machine.nome ?? `#${machine.id}`;
    }
    return acc;
  }, {});

  const resetDraft = (count: number) => {
    setDraft(buildDraft(count + 1));
  };

  const setEtapas = (next: FichaEtapa[]) => {
    const normalized = next.map((etapa, index) => ({
      ...etapa,
      sequencia: index + 1,
    }));
    setFieldValue('etapas', normalized);
  };

  const handleSave = () => {
    if (!draft.descricao.trim()) {
      return;
    }
    const sequenciaValue = Number(draft.sequencia);
    const newEtapa: FichaEtapa = {
      sequencia: Number.isNaN(sequenciaValue) || sequenciaValue <= 0 ? etapas.length + 1 : sequenciaValue,
      descricao: draft.descricao.trim(),
      observacao: draft.observacao.trim(),
      maquina: draft.maquina ?? null,
      tempo_setup_maquina: normalizeIntegerMinute(draft.tempo_setup_maquina),
      tempo_maquina_min: normalizeIntegerMinute(draft.tempo_maquina_min),
      tempo_homem_min: normalizeIntegerMinute(draft.tempo_homem_min),
      tempo_espera_min: normalizeIntegerMinute(draft.tempo_espera_min),
      qtd_operadores: draft.qtd_operadores ? Number(draft.qtd_operadores) : undefined,
    };
    const next = editingIndex !== null ? etapas.map((etapa, idx) => (idx === editingIndex ? newEtapa : etapa)) : [...etapas, newEtapa];
    setEtapas(next);
    resetDraft(next.length);
    setEditingIndex(null);
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= etapas.length) return;
    const next = [...etapas];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setEtapas(next);
  };

  const handleRemove = (index: number) => {
    const next = etapas.filter((_, idx) => idx !== index);
    setEtapas(next);
  };

  const handleEdit = (index: number) => {
    const etapa = etapas[index];
    setDraft({
      sequencia: String(etapa.sequencia ?? index + 1),
      descricao: etapa.descricao ?? '',
      observacao: etapa.observacao ?? '',
      maquina: etapa.maquina ?? undefined,
      tempo_setup_maquina: normalizeIntegerMinute(etapa.tempo_setup_maquina),
      tempo_maquina_min: normalizeIntegerMinute(etapa.tempo_maquina_min),
      tempo_homem_min: normalizeIntegerMinute(etapa.tempo_homem_min),
      tempo_espera_min: normalizeIntegerMinute(etapa.tempo_espera_min),
      qtd_operadores: etapa.qtd_operadores != null ? String(etapa.qtd_operadores) : '',
    });
    setEditingIndex(index);
  };

  return (
    <div className="p-4 border rounded bg-white">
      <h6 className="mb-3">Etapas</h6>
      <Row className="g-3">
        <Col md={1}>
          <Form.Group controlId="etapa-sequencia">
            <Form.Label>Sequência</Form.Label>
            <Form.Control
              type="number"
              value={draft.sequencia}
              onChange={(event) => setDraft((prev) => ({ ...prev, sequencia: event.target.value }))}
              min={1}
            />
          </Form.Group>
        </Col>
        <Col md={5}>
          <Form.Group controlId="etapa-descricao">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              value={draft.descricao}
              onChange={(event) => setDraft((prev) => ({ ...prev, descricao: event.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="etapa-maquina">
            <Form.Label>Máquina</Form.Label>
            <Form.Select
              value={draft.maquina ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  maquina: event.target.value ? Number(event.target.value) : undefined,
                }))
              }
            >
              <option value="">Selecione</option>
              {machines.map((machine) => (
                <option key={machine.id} value={machine.id}>
                  {machine.nome}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="etapa-qtd-operadores">
            <Form.Label>Operadores</Form.Label>
            <Form.Control
              type="number"
              value={draft.qtd_operadores}
              onChange={(event) => setDraft((prev) => ({ ...prev, qtd_operadores: event.target.value }))}
              min={0}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="g-3 mt-2">
        <Col md={3}>
          <Form.Group controlId="etapa-tempo-setup">
            <Form.Label>Tempo setup</Form.Label>
            <Form.Control
              type="number"
              value={draft.tempo_setup_maquina}
              onChange={(event) => setDraft((prev) => ({ ...prev, tempo_setup_maquina: event.target.value }))}
              placeholder="5"
              min={0}
              step={1}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="etapa-tempo-maquina">
            <Form.Label>Tempo máquina</Form.Label>
            <Form.Control
              type="number"
              value={draft.tempo_maquina_min}
              onChange={(event) => setDraft((prev) => ({ ...prev, tempo_maquina_min: event.target.value }))}
              placeholder="0"
              min={0}
              step={1}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="etapa-tempo-homem">
            <Form.Label>Tempo homem</Form.Label>
            <Form.Control
              type="number"
              value={draft.tempo_homem_min}
              onChange={(event) => setDraft((prev) => ({ ...prev, tempo_homem_min: event.target.value }))}
              placeholder="8"
              min={0}
              step={1}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="etapa-tempo-espera">
            <Form.Label>Tempo espera</Form.Label>
            <Form.Control
              type="number"
              value={draft.tempo_espera_min}
              onChange={(event) => setDraft((prev) => ({ ...prev, tempo_espera_min: event.target.value }))}
              placeholder="0"
              min={0}
              step={1}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="g-3 mt-2">
        <Col>
          <Form.Group controlId="etapa-observacao">
            <Form.Label>Observações</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={draft.observacao}
              onChange={(event) => setDraft((prev) => ({ ...prev, observacao: event.target.value }))}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="g-3 mt-1">
        <Col className="d-flex justify-content-end gap-2">
          {editingIndex !== null && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setEditingIndex(null);
                resetDraft(etapas.length);
              }}
            >
              Cancelar
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={handleSave}
            disabled={!draft.descricao.trim()}
          >
            {editingIndex !== null ? 'Atualizar etapa' : 'Adicionar etapa'}
          </Button>
        </Col>
      </Row>
      <Table responsive size="sm" bordered className="mt-4 mb-0">
        <thead>
          <tr>
            <th>Seq.</th>
            <th>Descrição</th>
            <th>Observação</th>
            <th>Máquina</th>
            <th>Setup (min)</th>
            <th>Máquina (min)</th>
            <th>Homem (min)</th>
            <th>Espera (min)</th>
            <th>Operadores</th>
            <th className="text-end">Ações</th>
          </tr>
        </thead>
        <tbody>
          {etapas.map((etapa, index) => (
            <tr key={`${etapa.sequencia}-${index}`}>
              <td>{etapa.sequencia}</td>
              <td>{etapa.descricao}</td>
              <td>{etapa.observacao}</td>
              <td>{normalizedMachines[etapa.maquina ?? -1] ?? '—'}</td>
              <td>{etapa.tempo_setup_maquina ?? '—'}</td>
              <td>{etapa.tempo_maquina_min ?? '—'}</td>
              <td>{etapa.tempo_homem_min ?? '—'}</td>
              <td>{etapa.tempo_espera_min ?? '—'}</td>
              <td>{etapa.qtd_operadores ?? '—'}</td>
              <td>
                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleMove(index, -1)}
                    disabled={index === 0}
                  >
                    <FiArrowUp />
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleMove(index, 1)}
                    disabled={index === etapas.length - 1}
                  >
                    <FiArrowDown />
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(index)}>
                    <FiEdit2 />
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleRemove(index)}>
                    <FiTrash2 />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
          {etapas.length === 0 && (
            <tr>
              <td colSpan={10} className="text-center text-muted">
                Nenhuma etapa cadastrada ainda.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

const InsumosTabContent: React.FC<{
  formData: Record<string, unknown>;
  setFieldValue: (name: string, value: unknown) => void;
}> = ({ formData, setFieldValue }) => {
  const insumos = Array.isArray(formData.insumos) ? (formData.insumos as FichaInsumo[]) : [];
  const [draft, setDraft] = useState<InsumoDraft>(buildInsumoDraft);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [productOptions, setProductOptions] = useState<SelectOption[]>([]);
  const [unitOptions, setUnitOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    produtoOptionLoader('')
      .then(setProductOptions)
      .catch(() => setProductOptions([]));
    unidadesMedidaService
      .list()
      .then((response) =>
        setUnitOptions(
          (response.results ?? []).map((unit) => ({
            value: unit.id!,
            label: `${unit.codigo ?? ''} ${unit.descricao ?? ''}`.trim() || `#${unit.id}`,
          })),
        ),
      )
      .catch(() => setUnitOptions([]));
  }, []);

  const productLabels = useMemo(() => {
    return productOptions.reduce<Record<number, string>>((acc, option) => {
      if (typeof option.value === 'number') {
        acc[option.value] = option.label;
      }
      return acc;
    }, {});
  }, [productOptions]);

  const unitLabels = useMemo(() => {
    return unitOptions.reduce<Record<number, string>>((acc, option) => {
      if (typeof option.value === 'number') {
        acc[option.value] = option.label;
      }
      return acc;
    }, {});
  }, [unitOptions]);

  const resetDraft = () => {
    setDraft(buildInsumoDraft());
  };

  const parsedQuantidadeTeorica = parseDecimalInput(draft.quantidade_teorica);
  const quantidadeValida = parsedQuantidadeTeorica !== undefined;

  const setInsumos = (next: FichaInsumo[]) => {
    const normalized = next.map((insumo) => ({
      ...insumo,
    }));
    setFieldValue('insumos', normalized);
  };

  const handleAdd = () => {
    if (!draft.produto || !quantidadeValida) {
      return;
    }
    const newInsumo: FichaInsumo = {
      produto: draft.produto,
      quantidade_teorica: parsedQuantidadeTeorica,
      unidade: draft.unidade ?? null,
      fator_aproveitamento: draft.fator_aproveitamento,
      observacao: draft.observacao.trim(),
    };
    const next = editingIndex !== null ? insumos.map((insumo, index) => (index === editingIndex ? newInsumo : insumo)) : [...insumos, newInsumo];
    setInsumos(next);
    resetDraft();
    setEditingIndex(null);
  };

  const handleEdit = (index: number) => {
    const insumo = insumos[index];
    setDraft({
      produto: insumo.produto,
      quantidade_teorica:
        insumo.quantidade_teorica != null ? String(insumo.quantidade_teorica) : '',
      unidade: insumo.unidade ?? undefined,
      fator_aproveitamento: insumo.fator_aproveitamento ?? '',
      observacao: insumo.observacao ?? '',
    });
    setEditingIndex(index);
  };

  const handleRemove = (index: number) => {
    const next = insumos.filter((_, idx) => idx !== index);
    setInsumos(next);
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= insumos.length) return;
    const next = [...insumos];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setInsumos(next);
  };

  return (
    <div className="p-4 border rounded bg-white">
      <h6 className="mb-3">Insumos</h6>
      <Row className="g-3">
        <Col md={3}>
          <Form.Group controlId="insumo-produto">
            <Form.Label>Produto</Form.Label>
            <Form.Select
              value={draft.produto ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  produto: event.target.value ? Number(event.target.value) : undefined,
                }))
              }
            >
              <option value="">Selecione</option>
              {productOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group controlId="insumo-quantidade">
            <Form.Label>Quantidade</Form.Label>
            <Form.Control
              value={draft.quantidade_teorica}
              onChange={(event) => setDraft((prev) => ({ ...prev, quantidade_teorica: event.target.value }))}
              placeholder="2.000"
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group controlId="insumo-unidade">
            <Form.Label>Unidade</Form.Label>
            <Form.Select
              value={draft.unidade ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  unidade: event.target.value ? Number(event.target.value) : undefined,
                }))
              }
            >
              <option value="">Selecione</option>
              {unitOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group controlId="insumo-fator">
            <Form.Label>Fator aproveitamento</Form.Label>
            <Form.Control
              value={draft.fator_aproveitamento}
              onChange={(event) => setDraft((prev) => ({ ...prev, fator_aproveitamento: event.target.value }))}
              placeholder="1.000"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="insumo-observacao">
            <Form.Label>Observação</Form.Label>
            <Form.Control
              value={draft.observacao}
              onChange={(event) => setDraft((prev) => ({ ...prev, observacao: event.target.value }))}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="g-3 mt-3">
        <Col className="d-flex justify-content-end gap-2">
          {editingIndex !== null && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setEditingIndex(null);
                resetDraft();
              }}
            >
              Cancelar
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={handleAdd}
            disabled={!draft.produto || !quantidadeValida}
          >
            {editingIndex !== null ? 'Atualizar insumo' : 'Adicionar insumo'}
          </Button>
        </Col>
      </Row>
      <Table responsive size="sm" bordered className="mt-4 mb-0">
        <thead>
          <tr>
            <th>Produto</th>
            <th>Quantidade</th>
            <th>Unidade</th>
            <th>Fator</th>
            <th>Observação</th>
            <th className="text-end">Ações</th>
          </tr>
        </thead>
        <tbody>
          {insumos.map((insumo, index) => (
            <tr key={`${insumo.produto ?? ''}-${index}`}>
              <td>{insumo.produto_descricao ?? productLabels[insumo.produto ?? -1] ?? `#${insumo.produto}`}</td>
              <td>{insumo.quantidade_teorica ?? '—'}</td>
              <td>{unitLabels[insumo.unidade ?? -1] ?? insumo.unidade ?? '—'}</td>
              <td>{insumo.fator_aproveitamento ?? '—'}</td>
              <td>{insumo.observacao ?? '—'}</td>
              <td>
                <div className="d-flex gap-2 justify-content-end">
                  <Button variant="outline-secondary" size="sm" onClick={() => handleMove(index, -1)} disabled={index === 0}>
                    <FiArrowUp />
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleMove(index, 1)}
                    disabled={index === insumos.length - 1}
                  >
                    <FiArrowDown />
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(index)}>
                    <FiEdit2 />
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleRemove(index)}>
                    <FiTrash2 />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
          {insumos.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                Nenhum insumo adicionado ainda.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

const SubprocessosTabContent: React.FC<{
  formData: Record<string, unknown>;
  setFieldValue: (name: string, value: unknown) => void;
}> = ({ formData, setFieldValue }) => {
  const subprocessos = Array.isArray(formData.subprocessos)
    ? (formData.subprocessos as FichaSubprocesso[])
    : [];
  const [draft, setDraft] = useState<SubprocessoDraft>(buildSubprocessoDraft);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [fichaOptions, setFichaOptions] = useState<SelectOption[]>([]);
  const [unitOptions, setUnitOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    fichaTecnicaService
      .list({ page_size: 999 })
      .then((response) => {
        const currentId =
          typeof formData.id === 'string'
            ? Number(formData.id)
            : typeof formData.id === 'number'
            ? formData.id
            : undefined;
        const options = (response.results ?? [])
          .filter((item) => item.id !== undefined && item.id !== currentId)
          .map((item) => ({
            value: item.id!,
            label: `${item.codigo ?? ''} ${item.titulo ?? ''}`.trim() || `Ficha ${item.id}`,
          }));
        setFichaOptions(options);
      })
      .catch(() => setFichaOptions([]));
    unidadesMedidaService
      .list()
      .then((response) =>
        setUnitOptions(
          (response.results ?? []).map((unit) => ({
            value: unit.id!,
            label: `${unit.codigo ?? ''} ${unit.descricao ?? ''}`.trim() || `#${unit.id}`,
          })),
        ),
      )
      .catch(() => setUnitOptions([]));
  }, [formData.id]);

  const fichaLabels = useMemo(() => {
    return fichaOptions.reduce<Record<number, string>>((acc, option) => {
      if (typeof option.value === 'number') {
        acc[option.value] = option.label;
      }
      return acc;
    }, {});
  }, [fichaOptions]);

  const unitLabels = useMemo(() => {
    return unitOptions.reduce<Record<number, string>>((acc, option) => {
      if (typeof option.value === 'number') {
        acc[option.value] = option.label;
      }
      return acc;
    }, {});
  }, [unitOptions]);

  const resetDraft = () => setDraft(buildSubprocessoDraft());

  const setSubprocessos = (next: FichaSubprocesso[]) => {
    const normalized = next.map((item, index) => ({
      ...item,
      sequencia: index + 1,
    }));
    setFieldValue('subprocessos', normalized);
  };

  const handleSave = () => {
    if (!draft.sub_ficha) {
      return;
    }
    const newSubprocesso: FichaSubprocesso = {
      sequencia: Number(draft.sequencia) || subprocessos.length + 1,
      sub_ficha: draft.sub_ficha,
      quantidade: draft.quantidade,
      unidade: draft.unidade ?? null,
      observacao: draft.observacao.trim(),
    };
    const next =
      editingIndex !== null
        ? subprocessos.map((item, index) => (index === editingIndex ? newSubprocesso : item))
        : [...subprocessos, newSubprocesso];
    setSubprocessos(next);
    resetDraft();
    setEditingIndex(null);
  };

  const handleEdit = (index: number) => {
    const item = subprocessos[index];
    setDraft({
      sequencia: String(item.sequencia ?? index + 1),
      sub_ficha: item.sub_ficha ?? undefined,
      quantidade: item.quantidade ?? '',
      unidade: item.unidade ?? undefined,
      observacao: item.observacao ?? '',
    });
    setEditingIndex(index);
  };

  const handleRemove = (index: number) => {
    const next = subprocessos.filter((_, idx) => idx !== index);
    setSubprocessos(next);
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= subprocessos.length) return;
    const next = [...subprocessos];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setSubprocessos(next);
  };

  return (
    <div className="p-4 border rounded bg-white">
      <h6 className="mb-3">Subprocessos</h6>
      <Row className="g-3">
        <Col md={1}>
          <Form.Group controlId="subprocesso-sequencia">
            <Form.Label>Sequência</Form.Label>
            <Form.Control
              type="number"
              value={draft.sequencia}
              onChange={(event) => setDraft((prev) => ({ ...prev, sequencia: event.target.value }))}
              min={1}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="subprocesso-ficha">
            <Form.Label>Sub-ficha</Form.Label>
            <Form.Select
              value={draft.sub_ficha ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  sub_ficha: event.target.value ? Number(event.target.value) : undefined,
                }))
              }
            >
              <option value="">Selecione</option>
              {fichaOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="subprocesso-quantidade">
            <Form.Label>Quantidade</Form.Label>
            <Form.Control
              value={draft.quantidade}
              onChange={(event) => setDraft((prev) => ({ ...prev, quantidade: event.target.value }))}
              placeholder="0.350"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="subprocesso-unidade">
            <Form.Label>Unidade</Form.Label>
            <Form.Select
              value={draft.unidade ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  unidade: event.target.value ? Number(event.target.value) : undefined,
                }))
              }
            >
              <option value="">Selecione</option>
              {unitOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="g-3 mt-2">
        <Col>
          <Form.Group controlId="subprocesso-observacao">
            <Form.Label>Observações</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={draft.observacao}
              onChange={(event) => setDraft((prev) => ({ ...prev, observacao: event.target.value }))}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="g-3 mt-1">
        <Col className="d-flex justify-content-end gap-2">
          {editingIndex !== null && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setEditingIndex(null);
                resetDraft();
              }}
            >
              Cancelar
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={handleSave} disabled={!draft.sub_ficha}>
            {editingIndex !== null ? 'Atualizar subprocesso' : 'Adicionar subprocesso'}
          </Button>
        </Col>
      </Row>
      <Table responsive size="sm" bordered className="mt-4 mb-0">
        <thead>
          <tr>
            <th>Seq.</th>
            <th>Sub-ficha</th>
            <th>Quantidade</th>
            <th>Unidade</th>
            <th>Observação</th>
            <th className="text-end">Ações</th>
          </tr>
        </thead>
        <tbody>
          {subprocessos.map((item, index) => (
            <tr key={`${item.sequencia}-${index}`}>
              <td>{item.sequencia}</td>
              <td>{fichaLabels[item.sub_ficha ?? -1] ?? `#${item.sub_ficha}`}</td>
              <td>{item.quantidade ?? '—'}</td>
              <td>{unitLabels[item.unidade ?? -1] ?? item.unidade ?? '—'}</td>
              <td>{item.observacao ?? '—'}</td>
              <td>
                <div className="d-flex gap-2 justify-content-end">
                  <Button variant="outline-secondary" size="sm" onClick={() => handleMove(index, -1)} disabled={index === 0}>
                    <FiArrowUp />
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleMove(index, 1)}
                    disabled={index === subprocessos.length - 1}
                  >
                    <FiArrowDown />
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(index)}>
                    <FiEdit2 />
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleRemove(index)}>
                    <FiTrash2 />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
          {subprocessos.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                Nenhum subprocesso adicionado ainda.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      </div>
    );
  };

const subprodutoTipoOptions = [
  { value: 'DESC', label: 'Descarte' },
  { value: 'REAP', label: 'Reaproveitamento' },
  { value: 'VEND', label: 'Venda' },
] as const;

const SubprodutosTabContent: React.FC<{
  formData: Record<string, unknown>;
  setFieldValue: (name: string, value: unknown) => void;
}> = ({ formData, setFieldValue }) => {
  const subprodutos = Array.isArray(formData.subprodutos)
    ? (formData.subprodutos as FichaSubproduto[])
    : [];
  const [draft, setDraft] = useState<SubprodutoDraft>(buildSubprodutoDraft);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [productOptions, setProductOptions] = useState<SelectOption[]>([]);
  const [unitOptions, setUnitOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    produtoOptionLoader('')
      .then(setProductOptions)
      .catch(() => setProductOptions([]));
    unidadesMedidaService
      .list()
      .then((response) =>
        setUnitOptions(
          (response.results ?? []).map((unit) => ({
            value: unit.id!,
            label: `${unit.codigo ?? ''} ${unit.descricao ?? ''}`.trim() || `#${unit.id}`,
          })),
        ),
      )
      .catch(() => setUnitOptions([]));
  }, []);

  const productLabels = useMemo(() => {
    return productOptions.reduce<Record<number, string>>((acc, option) => {
      if (typeof option.value === 'number') {
        acc[option.value] = option.label;
      }
      return acc;
    }, {});
  }, [productOptions]);

  const unitLabels = useMemo(() => {
    return unitOptions.reduce<Record<number, string>>((acc, option) => {
      if (typeof option.value === 'number') {
        acc[option.value] = option.label;
      }
      return acc;
    }, {});
  }, [unitOptions]);

  const resetDraft = () => setDraft(buildSubprodutoDraft());

  const setSubprodutos = (next: FichaSubproduto[]) => {
    const normalized = next.map((item, index) => ({
      ...item,
      sequencia: index + 1,
    }));
    setFieldValue('subprodutos', normalized);
  };

  const handleSave = () => {
    if (!draft.produto) {
      return;
    }
    const newSubproduto: FichaSubproduto = {
      produto: draft.produto,
      percentual_saida: draft.percentual_saida,
      tipo_subproduto: draft.tipo_subproduto,
      quantidade_estimada: draft.quantidade_estimada,
      unidade: draft.unidade ?? null,
      valor_venda: draft.valor_venda,
      observacao: draft.observacao.trim(),
    };
    const next =
      editingIndex !== null
        ? subprodutos.map((item, index) => (index === editingIndex ? newSubproduto : item))
        : [...subprodutos, newSubproduto];
    setSubprodutos(next);
    resetDraft();
    setEditingIndex(null);
  };

  const handleEdit = (index: number) => {
    const item = subprodutos[index];
    setDraft({
      produto: item.produto ?? undefined,
      percentual_saida: item.percentual_saida ?? '',
      tipo_subproduto: item.tipo_subproduto ?? undefined,
      quantidade_estimada: item.quantidade_estimada ?? '',
      unidade: item.unidade ?? undefined,
      valor_venda: item.valor_venda ?? '',
      observacao: item.observacao ?? '',
    });
    setEditingIndex(index);
  };

  const handleRemove = (index: number) => {
    const next = subprodutos.filter((_, idx) => idx !== index);
    setSubprodutos(next);
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= subprodutos.length) return;
    const next = [...subprodutos];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setSubprodutos(next);
  };

  return (
    <div className="p-4 border rounded bg-white">
      <h6 className="mb-3">Subprodutos</h6>
      <Row className="g-3">
        <Col md={3}>
          <Form.Group controlId="subproduto-produto">
            <Form.Label>Produto</Form.Label>
            <Form.Select
              value={draft.produto ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  produto: event.target.value ? Number(event.target.value) : undefined,
                }))
              }
            >
              <option value="">Selecione</option>
              {productOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group controlId="subproduto-percentual">
            <Form.Label>Percentual saída</Form.Label>
            <Form.Control
              value={draft.percentual_saida}
              onChange={(event) => setDraft((prev) => ({ ...prev, percentual_saida: event.target.value }))}
              placeholder="5.00"
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group controlId="subproduto-tipo">
            <Form.Label>Tipo</Form.Label>
            <Form.Select
              value={draft.tipo_subproduto ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  tipo_subproduto: event.target.value
                    ? (event.target.value as 'REAP' | 'DESC' | 'VEND')
                    : undefined,
                }))
              }
            >
              <option value="">Selecione</option>
              {subprodutoTipoOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group controlId="subproduto-quantidade">
            <Form.Label>Quantidade estimada</Form.Label>
            <Form.Control
              value={draft.quantidade_estimada}
              onChange={(event) => setDraft((prev) => ({ ...prev, quantidade_estimada: event.target.value }))}
              placeholder="0.017"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="subproduto-unidade">
            <Form.Label>Unidade</Form.Label>
            <Form.Select
              value={draft.unidade ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  unidade: event.target.value ? Number(event.target.value) : undefined,
                }))
              }
            >
              <option value="">Selecione</option>
              {unitOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="g-3 mt-2">
        <Col md={3}>
          <Form.Group controlId="subproduto-valor">
            <Form.Label>Valor de venda</Form.Label>
            <Form.Control
              value={draft.valor_venda}
              onChange={(event) => setDraft((prev) => ({ ...prev, valor_venda: event.target.value }))}
              placeholder="0.00"
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="subproduto-observacao">
            <Form.Label>Observação</Form.Label>
            <Form.Control
              value={draft.observacao}
              onChange={(event) => setDraft((prev) => ({ ...prev, observacao: event.target.value }))}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="g-3 mt-3">
        <Col className="d-flex justify-content-end gap-2">
          {editingIndex !== null && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setEditingIndex(null);
                resetDraft();
              }}
            >
              Cancelar
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={handleSave} disabled={!draft.produto}>
            {editingIndex !== null ? 'Atualizar subproduto' : 'Adicionar subproduto'}
          </Button>
        </Col>
      </Row>
      <Table responsive size="sm" bordered className="mt-4 mb-0">
        <thead>
          <tr>
            <th>Produto</th>
            <th>% Saída</th>
            <th>Tipo</th>
            <th>Quantidade</th>
            <th>Unidade</th>
            <th>Valor</th>
            <th>Observação</th>
            <th className="text-end">Ações</th>
          </tr>
        </thead>
        <tbody>
          {subprodutos.map((item, index) => (
            <tr key={`${item.produto ?? ''}-${index}`}>
              <td>{productLabels[item.produto ?? -1] ?? `#${item.produto}`}</td>
              <td>{item.percentual_saida ?? '—'}</td>
              <td>{item.tipo_subproduto ?? '—'}</td>
              <td>{item.quantidade_estimada ?? '—'}</td>
              <td>{unitLabels[item.unidade ?? -1] ?? item.unidade ?? '—'}</td>
              <td>{item.valor_venda ?? '—'}</td>
              <td>{item.observacao ?? '—'}</td>
              <td>
                <div className="d-flex gap-2 justify-content-end">
                  <Button variant="outline-secondary" size="sm" onClick={() => handleMove(index, -1)} disabled={index === 0}>
                    <FiArrowUp />
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleMove(index, 1)}
                    disabled={index === subprodutos.length - 1}
                  >
                    <FiArrowDown />
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(index)}>
                    <FiEdit2 />
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleRemove(index)}>
                    <FiTrash2 />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
          {subprodutos.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center text-muted">
                Nenhum subproduto adicionado ainda.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

const fields: ResourceField<FichaTecnica>[] = [
  { name: 'codigo', label: 'Codigo', placeholder: 'PTZ-001', colClass: 'col-md-3' },
  { name: 'titulo', label: 'Titulo', placeholder: 'Pizza Portuguesa - Grande', colClass: 'col-md-4' },
  {
    name: 'produto_final',
    label: 'Produto final',
    type: 'async-select',
    valueType: 'number',
    colClass: 'col-md-5',
    loadOptions: makeOptionLoader<ProdutoCatalogo>(
      catalogoProdutosService.list,
      (item) => {
        const parts = [];
        if (item.codigo) parts.push(item.codigo);
        if (item.descricao) parts.push(item.descricao);
        return parts.join(' - ') || String(item.id);
      },
    ),
    showSearchControl: false,
  },
  { name: 'versao', label: 'Versao', placeholder: '2.1', colClass: 'col-md-3' },
  {
    name: 'rendimento_quantidade',
    label: 'Rendimento (quant.)',
    placeholder: '0.000',
    colClass: 'col-md-3',
  },
  {
    name: 'rendimento_unidade',
    label: 'Unidade',
    type: 'async-select',
    valueType: 'number',
    colClass: 'col-md-3',
    loadOptions: makeOptionLoader<UnidadeMedida>(
      unidadesMedidaService.list,
      (item) => `${item.codigo ?? ''} ${item.descricao ?? ''}`.trim() || String(item.id),
    ),
    showSearchControl: false,
  },
  {
    name: 'ativa',
    label: 'Ficha ativa',
    colClass: 'col-md-3 d-flex align-items-end',
    render: ({ formData, setFieldValue }) => (
      <div className="w-100">
        <Form.Label className="form-label d-block">Ficha ativa</Form.Label>
        <Form.Check
          type="switch"
          id="ficha-ativa-switch"
          label="Ativa"
          checked={!!(formData.ativa as boolean | undefined)}
          onChange={(event) => setFieldValue?.('ativa', event.target.checked)}
        />
      </div>
    ),
  },
  {
    name: 'instrucoes_producao',
    label: 'Instruções de produção',
    type: 'textarea',
    placeholder: 'Descreva as instruções...',
    colClass: 'col-12',
  },
  {
    name: 'etapas',
    label: 'Etapas',
    hidden: true,
  },
  {
    name: 'insumos',
    label: 'Insumos',
    hidden: true,
  },
  {
    name: 'subprocessos',
    label: 'Subprocessos',
    hidden: true,
  },
  {
    name: 'subprodutos',
    label: 'Subprodutos',
    hidden: true,
  },
];

export const FichasFormPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('etapas');
  const initialValues = useMemo(
    () => ({
      etapas: [],
      insumos: [],
      subprocessos: [],
      subprodutos: [],
    }),
    [],
  );

  return (
    <section className="admin-shell__content">
      <div className="py-4 px-4 container-fluid">
        <ResourceFormPage
          title="Ficha técnica"
          description="Organize receitas, etapas e custos dos produtos."
          fields={fields}
          service={fichaTecnicaService}
          redirectPath="/admin/producao/fichas-tecnicas"
          cancelPath="/admin/producao/fichas-tecnicas"
          initialValues={initialValues}
          onSubmitPayload={(payload) => {
            console.log('[Ficha técnica] payload de salvamento:', payload);
          }}
          renderAfterActions={(formData, setFieldValue) => (
            <Row className="g-4 mt-3">
              <Col xs={12}>
                <Tab.Container
                  activeKey={activeTab}
                  onSelect={(eventKey) => setActiveTab(eventKey ?? 'etapas')}
                >
                  <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white border-0 px-0">
                      <Nav variant="tabs" fill>
                        {tabs.map((tab) => (
                          <Nav.Item key={tab.key}>
                            <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
                          </Nav.Item>
                        ))}
                      </Nav>
                    </Card.Header>
                    <Card.Body>
                      <Tab.Content>
                        <Tab.Pane eventKey="etapas">
                          <EtapasTabContent formData={formData} setFieldValue={setFieldValue} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="insumos">
                          <InsumosTabContent formData={formData} setFieldValue={setFieldValue} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="subprocessos">
                          <SubprocessosTabContent formData={formData} setFieldValue={setFieldValue} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="subprodutos">
                          <SubprodutosTabContent formData={formData} setFieldValue={setFieldValue} />
                        </Tab.Pane>
                      </Tab.Content>
                    </Card.Body>
                  </Card>
                </Tab.Container>
              </Col>
            </Row>
          )}
        />
      </div>
    </section>
  );
};


